import { gpCommon } from '../app';
import { ALL_CAPABILITY_KEYS } from '../util/capabilities';
import { capabilityReasonFor, planContainsCapability } from '../util/capability-context';
import { isCapabilityGatingEnabled } from '../util/feature-flags';
import {
    CAPABILITY_GATING_CLASSES,
    gatingClassFor,
    parseWithheldIntent,
    resolvePresentation,
    shouldRenderSubstitute,
} from '../util/capability-presentation';
import { isKnownCapabilityKey } from './capability-keys';

import type * as ng from 'angular';

/**
 * The capability gating attributes (see docs/capability-gating-ui.md).
 *
 *     <li gp-capability="templates">Create template</li>
 *     <li gp-capability="msProjectImport" gp-withheld="entice">MS Project</li>
 *     <button gp-capability="shareByEmail" gp-withheld="disable">Change</button>
 *     <li gp-capability="equipmentResources" gp-keep-if-in-plan>…</li>
 *     <div gp-if-withheld="costing">Costing is a Pro feature</div>
 *
 * Switched by the CapabilityGating feature flag, which is AppliesPerUser, so
 * gating can be turned on for one account on any environment through the
 * AdminPortal. With the flag off both directives are inert — and the SERVER
 * independently resolves every capability to its ungated value under the same
 * flag, so the two halves cannot disagree about whether gating is on.
 *
 * Every gated element carries its decision as `data-gp-presentation`, alongside
 * `data-gp-capability` and `data-gp-withheld`. That is how you answer "why is
 * this control not here?" — the element is still in the DOM, hidden by a class,
 * with the reason attached.
 */

gpCommon.directive('gpCapability', ['$injector', function ($injector: ng.auto.IInjectorService): ng.IDirective {
    return {
        restrict: 'A',
        link: function ($scope: ng.IScope, $element: ng.IAugmentedJQuery, $attrs: ng.IAttributes): void {
            if (!isCapabilityGatingEnabled()) {
                return;
            }

            const element = $element[0];
            const key = ($attrs as { gpCapability?: string }).gpCapability?.trim() ?? '';

            // A typo must be loud. An unknown key resolves to notEntitled,
            // which is indistinguishable from a capability legitimately
            // switched off — so a misspelling silently deletes a control and
            // reads as a working gate until somebody checks.
            if (!isKnownCapabilityKey(key)) {
                console.error(
                    `gp-capability: "${key}" is not a capability. Expected one of: `
                    + `${ALL_CAPABILITY_KEYS.join(', ')}`,
                    element);
                element.classList.add('gp-capability-invalid');
                element.setAttribute('data-gp-capability', key);
                return;
            }

            const intent = parseWithheldIntent(($attrs as { gpWithheld?: string }).gpWithheld);
            const keepIfInPlan = 'gpKeepIfInPlan' in $attrs;

            element.setAttribute('data-gp-capability', key);
            element.setAttribute('data-gp-withheld', intent);

            // Watched rather than read once, so a capability changed on /debug
            // or granted by an administrator repaints as soon as the snapshot
            // refills — every subscriptionstatus response calls
            // gpSetCapabilities — without a page reload. Both reads are
            // in-memory lookups, cheap enough to run every digest.
            $scope.$watch(
                () => {
                    const reason = capabilityReasonFor($injector, key);
                    const inPlan = keepIfInPlan && planContainsCapability($injector, key);
                    return resolvePresentation({ reason, intent, keepIfInPlan, isInPlan: inPlan });
                },
                presentation => {
                    element.setAttribute('data-gp-presentation', presentation);
                    element.classList.remove(...CAPABILITY_GATING_CLASSES);

                    const gatingClass = gatingClassFor(presentation);
                    if (gatingClass) {
                        element.classList.add(gatingClass);
                    }
                });
        },
    };
}]);

/**
 * The substitute half: an element that renders ONLY when the capability is
 * withheld by licence — an upsell panel at a destination that can carry the
 * pitch itself, such as the Costs tab.
 *
 * Nothing is generated. What goes inside is written per site, because "one
 * visible touchpoint per capability" (#510) is a per-site decision and a
 * generated banner would appear at all of them.
 *
 * Independent of the CapabilityGating flag, unlike `gpCapability`: with gating
 * off every capability resolves to available, so the substitute is absent
 * anyway, and checking the flag as well would only add a way for the two halves
 * to disagree.
 */
gpCommon.directive('gpIfWithheld', ['$injector', function ($injector: ng.auto.IInjectorService): ng.IDirective {
    return {
        restrict: 'A',
        link: function ($scope: ng.IScope, $element: ng.IAugmentedJQuery, $attrs: ng.IAttributes): void {
            const element = $element[0];
            const key = ($attrs as { gpIfWithheld?: string }).gpIfWithheld?.trim() ?? '';

            if (!isKnownCapabilityKey(key)) {
                console.error(
                    `gp-if-withheld: "${key}" is not a capability. Expected one of: `
                    + `${ALL_CAPABILITY_KEYS.join(', ')}`,
                    element);
                element.classList.add('gp-capability-invalid');
                return;
            }

            element.setAttribute('data-gp-if-withheld', key);

            // Stamping data-gp-presentation is what releases the panel: until
            // it appears, capability-gating.less holds every gp-if-withheld
            // element hidden, so an upsell cannot flash at a paying customer in
            // the window between parse and link.
            $scope.$watch(
                () => shouldRenderSubstitute(capabilityReasonFor($injector, key)),
                withheld => {
                    element.setAttribute(
                        'data-gp-presentation', withheld ? 'substitute-shown' : 'substitute-idle');

                    element.classList.remove(...CAPABILITY_GATING_CLASSES);

                    if (!withheld) {
                        element.classList.add('gp-capability-hidden');
                    }
                });
        },
    };
}]);
